import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell, Field, ErrorBanner } from "./ForgotPassword.jsx";
import { apiFetch } from "../lib/api.js";

/**
 * Generic "set your password" form, used for both welcome (type=welcome)
 * and reset (type=reset) flows. The only difference is which API endpoint
 * consumes the token — the UI/text is near-identical.
 *
 * On mount: preflights the token via GET /api/auth/token-check so we can
 * show a clear "this link is expired" page instead of letting the user
 * type a new password into a form that will fail on submit.
 */
export function SetPasswordPage({ type = "welcome" }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [checking, setChecking] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [tokenMeta, setTokenMeta] = useState(null);

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setChecking(false);
      return;
    }
    apiFetch(`/api/auth/token-check?token=${encodeURIComponent(token)}&type=${type}`)
      .then((r) => {
        if (cancelled) return;
        setTokenValid(!!r.valid);
        setTokenMeta(r);
      })
      .catch(() => { if (!cancelled) setTokenValid(false); })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, [token, type]);

  async function submit(e) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setSubmitting(true);
    try {
      const path = type === "reset" ? "/api/auth/reset-password" : "/api/auth/set-password";
      await apiFetch(path, { method: "POST", body: JSON.stringify({ token, password }) });
      setDone(true);
      // Bounce to login after a beat so the success state is visible.
      setTimeout(() => navigate("/login", { replace: true }), 1500);
    } catch (err) {
      setError(err.message || "Could not set password");
    } finally {
      setSubmitting(false);
    }
  }

  if (checking) {
    return <AuthShell title="Checking link…" subtitle="One moment." />;
  }

  if (!token || !tokenValid) {
    return (
      <AuthShell
        title="This link isn't valid"
        subtitle={
          type === "reset"
            ? "The reset link is expired or already used. Request a new one from the sign-in page."
            : "This welcome link is expired or already used. Ask your admin to resend a new invite."
        }
      >
        <Link
          to={type === "reset" ? "/forgot-password" : "/login"}
          className="block w-full text-center h-10 leading-10 rounded bg-accent text-white text-[13px] font-semibold hover:opacity-90 transition-opacity"
        >
          {type === "reset" ? "Request a new link" : "Back to sign in"}
        </Link>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password set" subtitle="Redirecting you to sign in…">
        <Link to="/login" className="block w-full text-center h-10 leading-10 rounded border border-border-default text-[13px] text-text-secondary hover:bg-[var(--color-border-light)]">
          Go to sign in now
        </Link>
      </AuthShell>
    );
  }

  const headline = type === "reset" ? "Set a new password" : "Welcome! Set your password";
  const subtitle = tokenMeta?.email
    ? `For ${tokenMeta.email}. Pick something at least 8 characters.`
    : "Pick something at least 8 characters.";

  return (
    <AuthShell title={headline} subtitle={subtitle}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        <Field
          label="New password" type="password" value={password}
          onChange={setPassword} autoFocus required autoComplete="new-password"
          help="At least 8 characters."
        />
        <Field
          label="Confirm password" type="password" value={confirm}
          onChange={setConfirm} required autoComplete="new-password"
        />
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <button
          type="submit" disabled={submitting}
          className="w-full h-10 rounded bg-accent text-white text-[13px] font-semibold disabled:opacity-60 hover:opacity-90 transition-opacity"
        >
          {submitting ? "Saving…" : "Set password"}
        </button>
      </form>
    </AuthShell>
  );
}

/** Welcome flow — same component, type=welcome. */
export function SetPassword()   { return <SetPasswordPage type="welcome" />; }

/** Reset flow — same component, type=reset. */
export function ResetPassword() { return <SetPasswordPage type="reset" />; }
